import { Users } from 'lucide-react';
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setSubLoading } from '../../Redux/Services/AuthSlice';
import { NotificationSound } from '../CommonComponents/CommonNotification';
import { ApiCall, convertToNumber, deleteData, sanitizeInput } from '../CommonComponents/CustomHooks';
import { CommonApiData, initializeFilterData } from '../CommonComponents/EnvConfig';
import { handleDownloadExcel } from '../CommonComponents/ExportData';
import { AutoCompleteBoxNew, FormButton, InputBox, Switch } from '../CommonComponents/InputBox';
import { Table } from '../CommonComponents/Table';

const PickupPointMaster = () => {
    const inputrefs = useRef([])
    const dispatch = useDispatch([])
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([])
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData)
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });
    const loginUserClientCode = CommonApiData().loginUserClientCode;
    const initialFormData = {
        pickupID: '',
        pickupPointName: '',
        client: loginUserClientCode,
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        mobileNo: '',
        driver: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        active: true,
    }

    const [formData, setFormData] = useState(initialFormData);

    const getPickupPointMasterData = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        const data = {
            ...filterData,
            Type: 'S',
            PageIndex: `${PageIndex}`,
            ...sortConfig
        }
        setIsLoading(true)
        let res = await ApiCall(`/api/Shipment/ShowPickupPointMaster`, data)
        if (res.Status === 'SUCCESS') {
            setTableData(res)
        }
        if (res.Status === 'ERROR') {
            setTableData([])
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED') {
            await NotificationSound(res.Status, res.Message, res.focus, inputrefs);
        }
    }

    const handleClear = () => {
        setFormData(initialFormData)
        filterData.SearchText = ''
        inputrefs.current['txtPickupPointName'].focus()
        getPickupPointMasterData()
    }

    const handleSubmit = async () => {
        await dispatch(setSubLoading(true))
        let data = await ApiCall(`/api/Shipment/InsertUpdatePickupPointMaster`, { ...formData })
        await dispatch(setSubLoading(false))
        await NotificationSound(data.status, data.message, data.focus, inputrefs, handleClear);
    };

    const handleEditData = async (id) => {
        await dispatch(setSubLoading(true))
        let editData = await ApiCall(`/api/Shipment/EditPickupPointMaster`, { pickupID: id })
        await dispatch(setSubLoading(false))
        if (editData.status === 'SUCCESS') {
            let record = editData.data
            setFormData((pre) => ({
                ...pre, ...record
            }))
            await inputrefs.current[editData.focus].focus();
        }
        else {
            await NotificationSound(editData.status, editData.message, editData.focus, inputrefs);
        }
    }

    const GetCityStateByPincode = async (pincode) => {
        await dispatch(setSubLoading(true));
        let data = await ApiCall(`/api/Dashboard/GetPinCodeByCityState`, { pincode: pincode || "" });
        await dispatch(setSubLoading(false));
        if (data?.status === 'SUCCESS') {
            setFormData(prev => ({
                ...prev,
                city: data?.city || "",
                state: data?.state || "",
                pincode: pincode || "",
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                city: "",
                state: "",
                pincode: pincode || "",
            }));
        }
    };

    const handleDelete = async (id) => {
        await dispatch(setSubLoading(true))
        await deleteData(`/api/Shipment/DeletePickupPointMaster`, { pickupID: id }, getPickupPointMasterData, handleClear, '', inputrefs)
        await dispatch(setSubLoading(false))
    }

    const handleChange1 = (event, value) => {
        getPickupPointMasterData(value);
    };

    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
    };

    useEffect(() => {
        inputrefs.current['txtPickupPointName'].focus()
        document.title = 'Pickup Point Master'
        getPickupPointMasterData()
    }, [sortConfig]);

    return (
        <>
            <div className='bg-secondary-50 shadow-lg rounded-md p-3 pt-2'>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary-600" />
                        <h2 className="text-xl font-semibold text-primary-600">Pickup Point Master</h2>
                    </div>
                </div>
                <div className='card-body pb-1'>
                    <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>

                        <InputBox
                            id="txtPickupPointName"
                            required
                            label="Pickup Point Name"
                            placeholder="Enter Pickup Point Name"
                            inputrefs={inputrefs}
                            value={formData.pickupPointName || ''}
                            maxLength='50'
                            onChange={(e) => setFormData({ ...formData, pickupPointName: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtAddressLine1"].focus();
                            }}
                        />

                        <AutoCompleteBoxNew
                            id="txtClient"
                            label="Client"
                            disabled
                            placeholder="Select Client"
                            inputrefs={inputrefs}
                            autocompleteType="GetClient"
                            value={formData.client || ''}

                            onChange={async (event, newInputValue) => {
                                newInputValue = newInputValue || ''
                                if (event.type !== 'keydown') {
                                    setFormData({ ...formData, client: newInputValue })
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtAddressLine1"].focus();
                            }}
                        />

                        <InputBox
                            id="txtAddressLine1"
                            label="Address Line 1"
                            required
                            placeholder="Enter Address Line 1"
                            inputrefs={inputrefs}
                            maxLength='250'
                            value={formData.addressLine1 || ''}
                            onChange={(e) => setFormData({ ...formData, addressLine1: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtAddressLine2"].focus();
                            }}
                        />

                        <InputBox
                            id="txtAddressLine2"
                            label="Address Line 2"
                            placeholder="Enter Address Line 2"
                            maxLength='250'
                            inputrefs={inputrefs}
                            value={formData.addressLine2 || ''}
                            onChange={(e) => setFormData({ ...formData, addressLine2: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtAddressLine3"].focus();
                            }}
                        />

                        <InputBox
                            id="txtAddressLine3"
                            label="Address Line 3"
                            placeholder="Enter Address Line 3"
                            maxLength='250'
                            inputrefs={inputrefs}
                            value={formData.addressLine3 || ''}
                            onChange={(e) => setFormData({ ...formData, addressLine3: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtMobileNo"].focus();
                            }}
                        />

                        <InputBox
                            id="txtMobileNo"
                            label="Mobile No"
                            required
                            maxlength="10"
                            placeholder="Enter Mobile No"
                            inputrefs={inputrefs}
                            value={formData.mobileNo || ''}
                            onChange={(e) => setFormData({ ...formData, mobileNo: convertToNumber(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtDriver"].focus();
                            }}
                        />

                        <InputBox
                            id="txtDriver"
                            label="Driver Name"
                            placeholder="Enter Driver Name"
                            inputrefs={inputrefs}
                            maxLength='50'
                            value={formData.driver || ''}
                            onChange={(e) => setFormData({ ...formData, driver: sanitizeInput(e.target.value) })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtPinCode"].focus();
                            }}
                        />

                        <AutoCompleteBoxNew
                            id="txtPinCode"
                            label="Pin Code"
                            required
                            placeholder="Enter Pin Code"
                            inputrefs={inputrefs}
                            autocompleteType="GetPinCode"
                            value={formData.pincode || ''}
                            onChange={async (event, newInputValue) => {
                                newInputValue = newInputValue || '';
                                if (event.type !== 'keydown') {
                                    const updatedData = { ...formData, pincode: newInputValue };
                                    setFormData(updatedData);
                                    GetCityStateByPincode(newInputValue);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    inputrefs.current['txtCity'].focus()
                                }
                            }}
                        />

                        <AutoCompleteBoxNew
                            id="txtCity"
                            label="City"
                            required
                            placeholder="Select City"
                            inputrefs={inputrefs}
                            autocompleteType="GetCity"
                            value={formData.city || ''}

                            onChange={async (event, newInputValue) => {
                                newInputValue = newInputValue || ''
                                if (event.type !== 'keydown') {
                                    setFormData({ ...formData, city: newInputValue })
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtState"].focus();
                            }}
                        />

                        <AutoCompleteBoxNew
                            id="txtState"
                            label="State"
                            required
                            placeholder="Select State"
                            inputrefs={inputrefs}
                            autocompleteType="GetState"
                            value={formData.state || ''}
                            onChange={async (event, newInputValue) => {
                                newInputValue = newInputValue || ''
                                if (event.type !== 'keydown') {
                                    setFormData({ ...formData, state: newInputValue })
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') inputrefs.current["txtActive"].focus();
                            }}
                        />
                        <Switch
                            label='Active'
                            checked={formData.active}
                            id='txtActive'
                            inputrefs={inputrefs}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    inputrefs.current['btnSub'].focus()
                                };
                            }}
                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                            divclassname="mt-2"
                        />
                    </div>
                </div>
                <div className='card-body'>
                    <FormButton
                        id={'btnSub'}
                        inputrefs={inputrefs}
                        handleSubmit={handleSubmit}
                        handleExport={async () => {
                            await dispatch(setSubLoading(true))
                            handleDownloadExcel(`/api/Shipment/ShowPickupPointMaster`, filterData, 'Pickup Point Master')
                            await dispatch(setSubLoading(false))
                        }}
                        handleClear={handleClear}
                    />
                </div>
            </div>
            <div className="mt-3">
            <Table
                handleEdit={handleEditData}
                handleDelete={handleDelete}
                loading={isLoading}
                PageName={'PickupPointMaster'}
                handleFilter={() => {
                    getPickupPointMasterData()
                    setFilterData({
                        ...filterData,
                    })
                }}
                filterData={filterData}
                tableData={tableData}
                setFilterData={setFilterData}
                currentPageNo={currentPageNo}
                handleChange1={handleChange1}
                handleSorting={handleSorting}
                sortConfig={sortConfig}
                />
            </div>
        </>
    )
}
export default PickupPointMaster;